/**
 * WEB-1217 (хвост, найденный разбором пачки 16.08) — the response-interceptor
 * DECISION for a 403 EMAIL_NOT_VERIFIED failure: retry once after a silent
 * refresh, land the user on /verify-email, or leave the error to fall
 * through untouched. Split out of client.ts for the same reason as
 * refreshExemptPaths.ts, formDataContentType.ts, emailNotVerifiedFallback.ts
 * and tokenRefresh.ts before it — client.ts reads `import.meta.env` at
 * module scope, which ts-jest cannot import directly, so the testable seam
 * has to live in its own framework-free module. This is the fifth split on
 * that same seam.
 *
 * EmailVerifiedGuard (WEB-420) reads isEmailVerified from the JWT itself,
 * not the DB. verifyEmailThunk already reissues a fresh token right after
 * verification, so this is insurance for a token that's stale for some
 * OTHER reason (verified in a different tab/device, an old token from
 * before this fix, etc.).
 *
 * Deliberately dependency-injected — no axios, no localStorage, no antd —
 * mirroring tokenRefresh.ts: the actual HTTP retry and the actual redirect
 * stay in client.ts as thin wiring; this module only DECIDES what to do.
 */

export interface EmailNotVerifiedErrorInfo {
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly url: string | undefined;
  /**
   * Mirrors RetryableRequestConfig._emailNotVerifiedRetry in client.ts: true
   * only when THIS invocation is itself the retried request recursing back
   * into the interceptor and failing again — i.e. the one-retry budget for
   * this original request has already been spent.
   */
  readonly alreadyRetried: boolean;
}

export interface EmailNotVerifiedRetryDeps {
  /**
   * Whether a refresh token is currently stored. Read BEFORE attempting the
   * refresh — distinguishes "nothing to refresh with" (silent today, see
   * tokenRefresh.ts's doRefresh) from "had a refresh token but the server
   * rejected it" (refreshCoordinator's own onSessionExpired already fired
   * its own toast+logout — redirecting here too would be a confusing double
   * message for an unrelated cause: a truly revoked session, not an
   * unverified email).
   */
  readonly hasStoredRefreshToken: () => boolean;
  /** Attempts a silent token refresh; resolves with the new access token. */
  readonly refreshAccessToken: () => Promise<string>;
  /**
   * WEB-776 — /invites/* (AcceptInvite.tsx) already has its own, more
   * specific inline handling of EMAIL_NOT_VERIFIED. A redirect from here
   * would silently override that screen, so exempt URLs must never redirect
   * — on the first encounter OR the terminal one.
   */
  readonly isExemptUrl: (url: string | undefined) => boolean;
}

export type EmailNotVerifiedDecision =
  | { readonly action: 'retry'; readonly accessToken: string }
  | { readonly action: 'redirect' }
  | { readonly action: 'pass' };

export async function resolveEmailNotVerifiedRetry(
  info: EmailNotVerifiedErrorInfo,
  deps: EmailNotVerifiedRetryDeps,
): Promise<EmailNotVerifiedDecision> {
  const isEmailNotVerified =
    info.status === 403 && info.code === 'EMAIL_NOT_VERIFIED';
  if (!isEmailNotVerified) {
    // AC — a 401 (or any other failure) is untouched by this factory: the
    // 401/session-expired branch in client.ts owns its own message, and
    // mixing the two here would risk a request seeing both.
    return { action: 'pass' };
  }

  if (info.alreadyRetried) {
    // Terminal: the one retry budget for this request is already spent —
    // never call refreshAccessToken again here. Doing so on every repeated
    // EMAIL_NOT_VERIFIED failure is exactly the silent infinite-retry-loop
    // risk this guard exists to close.
    return deps.isExemptUrl(info.url)
      ? { action: 'pass' }
      : { action: 'redirect' };
  }

  const hadRefreshToken = deps.hasStoredRefreshToken();
  try {
    const accessToken = await deps.refreshAccessToken();
    return { action: 'retry', accessToken };
  } catch {
    if (!hadRefreshToken && !deps.isExemptUrl(info.url)) {
      return { action: 'redirect' };
    }
    return { action: 'pass' };
  }
}
